import * as React from "react";
import { useState } from "react";

import { Button, styled, Theme } from "@mui/material";
import { RosebudColorMap } from "../rosebud/rosebudColorMap";
import { useGroupQualifiersByGroup } from "./refinementHooks";
import { RefineInterface } from "./RefineInterface";
import { CondensedTypographyMedium } from "fe-web-components/styles/typography";
import { KahunColorMap } from "fe-common/ui/assets/colorMap";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { RelationRow } from "./relationRow";
import { MobileView } from "../../../util/aKaunGeneralUtil";

const RefinementRow = styled("div")(() => ({
  display: "flex",
  flexDirection: "column",
  paddingTop: "5px",
  paddingBottom: "5px",
}));

export const RelationName = styled("div")(({ theme }) => ({
  minWidth: "100px",
  fontSize: "16px",
  fontWeight: 400,

  color: RosebudColorMap.gray1,
}));

const MoreChip = styled(Button)({
  fontSize: "14px",
  marginRight: "10px",
  textTransform: "capitalize",
  marginBottom: "10px",
  marginTop: "10px",

  height: "42px",

  backgroundColor: "transparent",
  border: "1px solid " + KahunColorMap.DefaultLight,
  color: KahunColorMap.DefaultLight,
});

const MoreText = styled(CondensedTypographyMedium)({
  color: RosebudColorMap.AccentLight,
  display: "flex",
  alignItems: "center",
  fontSize: "16px",
});

const MoreChipContainer = styled("div")(
  ({ theme, popup }: { theme?: Theme; popup: string }) => ({
    width: "100%",
    display: "flex",
    justifyContent: popup === "true" ? "flex-end" : "flex-start",
  }),
);

const RelationValues = styled("div")(({ theme }) => ({
  marginBottom: theme.spacing(1),
  display: "flex",
  flexWrap: "wrap",
}));

export const QualifierValue = styled(Button)(
  ({ theme, clicked }: { theme?: Theme; clicked: "true" | "false" }) => ({
    marginRight: "8px",
    marginBottom: "5px",
    marginTop: "5px",
    borderRadius: "5px",
    fontSize: "14px",
    textAlign: "left",
    height: "34px",
    fontWeight: 400,

    [theme.breakpoints.down(MobileView)]: {
      minHeight: "34px",
      marginBottom: "5px",
      marginTop: "5px",
      height: "unset",
    },

    textTransform: "capitalize",
    "&:focus, &:active, &:hover": {
      backgroundColor:
        clicked === "true" ? KahunColorMap.PrimaryDim : "transparent",
      borderColor: clicked === "true" ? "white" : KahunColorMap.PrimaryDim,
      color: clicked === "true" ? "white" : KahunColorMap.PrimaryDim,
    },
    backgroundColor:
      clicked === "true" ? KahunColorMap.PrimaryDim : "transparent",
    borderColor: clicked === "true" ? "white" : KahunColorMap.PrimaryDim,
    color: clicked === "true" ? "white" : KahunColorMap.PrimaryDim,
  }),
);

export const MoreQualifierValue = styled(Button)(
  ({ theme, clicked }: { theme?: Theme; clicked: "true" | "false" }) => ({
    marginRight: "8px",
    marginBottom: "5px",
    borderRadius: "5px",
    marginTop: "5px",
    fontSize: "14px",
    textAlign: "left",
    height: "34px",
    fontWeight: 400,

    [theme.breakpoints.down(MobileView)]: {
      minHeight: "34px",
      marginBottom: "5px",
      marginTop: "5px",
      height: "unset",
    },

    textTransform: "capitalize",

    "&:focus, &:active, &:hover": {
      backgroundColor:
        clicked === "true" ? KahunColorMap.PrimaryDim : "transparent",
      borderColor: clicked === "true" ? "white" : KahunColorMap.PrimaryDim,
      color: clicked === "true" ? "white" : KahunColorMap.PrimaryDim,
    },
    backgroundColor:
      clicked === "true" ? KahunColorMap.PrimaryDim : "transparent",
    borderColor: clicked === "true" ? "white" : KahunColorMap.PrimaryDim,
    color: clicked === "true" ? "white" : KahunColorMap.PrimaryDim,
  }),
);

const MAX_VALUES = 4;

export const RefineByRelation = ({
  qualifiers,
  causedByDisease,
  qf,
  onRemove,
  onAdd,
  numRelations,
  hideClinicalReasoning,
  onRefineMore,
  orderedRelations,
  disabled,
}: RefineInterface) => {
  const [showMore, setShowMore] = useState({});

  let relationToQualifiers = useGroupQualifiersByGroup(qualifiers);

  const slicedOrderedRelations = numRelations
    ? orderedRelations.slice(0, numRelations)
    : orderedRelations;

  const handleShowMore = (relation) => {
    setShowMore({
      ...showMore,
      [relation]: !showMore[relation],
    });
  };

  const RelationRows = ({ relation }) => {
    return (
      <RefinementRow key={relation}>
        <RelationName className={"relation-name"}>{relation}</RelationName>
        <RelationValues>
          {relationToQualifiers[relation].map((q, valueIndex) => (
            <React.Fragment key={valueIndex}>
              {(valueIndex < MAX_VALUES || showMore[relation]) && (
                <RelationRow
                  key={valueIndex}
                  qualifier={q}
                  qf={qf}
                  onRemove={onRemove}
                  onAdd={onAdd}
                  disabled={disabled}
                  causedByDisease={causedByDisease}
                />
              )}
            </React.Fragment>
          ))}
          {relationToQualifiers[relation].length > MAX_VALUES && (
            <MoreQualifierValue
              variant={"outlined"}
              size="small"
              disabled={disabled}
              clicked={"false"}
              onClick={() => {
                handleShowMore(relation);
              }}
            >
              {showMore[relation] ? "Less..." : "More..."}
            </MoreQualifierValue>
          )}
        </RelationValues>
      </RefinementRow>
    );
  };

  //
  return (
    <>
      {slicedOrderedRelations.map((relation, index) =>
        relationToQualifiers[relation].length > 0 ? (
          <RelationRows key={`relation-row-${index}`} relation={relation} />
        ) : null,
      )}

      {numRelations < orderedRelations.length && onRefineMore && (
        <MoreChipContainer popup={"true"}>
          <MoreChip
            disabled={disabled}
            onClick={() => onRefineMore(qf)}
            variant={"outlined"}
            size={"medium"}
          >
            <MoreText>
              Show More
              <ArrowDropDownIcon />
            </MoreText>
          </MoreChip>
        </MoreChipContainer>
      )}
    </>
  );
};
